<div
  class="edu-generated flex flex-col gap-6 w-full text-start"
  [class.edu-generated--embed]="embedMode">
  <header class="edu-generated__header border-b border-slate-200 pb-4">
    @if (view.title) {
      <h2
        [ngClass]="
          embedMode
            ? 'h5 fw-bold text-body mb-2'
            : 'text-2xl lg:text-3xl font-bold text-blue-900 m-0 mb-2 leading-snug'
        ">
        {{ view.title }}
      </h2>
    }
    @if (view.titleTranslation) {
      <p
        class="text-slate-600 m-0 mb-2 leading-relaxed"
        [class.small]="embedMode">
        {{ view.titleTranslation }}
      </p>
    }
    <div class="flex flex-wrap gap-2">
      @if (view.contentType) {
        <span
          class="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-blue-50 text-blue-700"
          >{{ 'knowledgeSetup.contentType' | translate }}: {{ view.contentType | titlecase }}</span
        >
      }
      @if (view.topicDomain) {
        <span
          class="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-violet-50 text-violet-800"
          >{{ 'knowledgeSetup.extraFields.topicDomain' | translate }}: {{ view.topicDomain }}</span
        >
      }
      @if (view.learningFocus) {
        <span
          class="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-amber-50 text-amber-900"
          >{{ 'knowledgeSetup.extraFields.learningFocus' | translate }}: {{ view.learningFocus }}</span
        >
      }
      @if (view.standardLevel) {
        <span
          class="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-emerald-50 text-emerald-800"
          >{{ 'knowledgeSetup.extraFields.standardLevel' | translate }}: {{ view.standardLevel }}</span
        >
      }
      @if (view.contentLanguage) {
        <span
          class="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold bg-slate-100 text-slate-700"
          >{{ 'knowledgeSetup.contentLanguage' | translate }}: {{ view.contentLanguage }}</span
        >
      }
    </div>
  </header>

  @if (view.description) {
    <section class="rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
      <h3 [ngClass]="headingClass()">{{ 'knowledgeSetup.sectionDescription' | translate }}</h3>
      <p
        class="text-slate-700 m-0 leading-relaxed whitespace-pre-wrap"
        [class.small]="embedMode">
        {{ view.description }}
      </p>
    </section>
  }

  @if (showPrimarySteps()) {
    <section class="edu-generated__primary-steps">
      <h3 [ngClass]="headingClass()">{{ primaryLabelKey() | translate }}</h3>
      <ol class="m-0 ps-5 space-y-2 list-decimal text-slate-700" [class.small]="embedMode">
        @for (step of view.activitySteps; track $index) {
          <li class="ps-1 leading-relaxed">{{ itemLabel(step) }}</li>
        }
      </ol>
    </section>
  }

  @if (showPrimaryBody()) {
    <section class="edu-generated__primary-body">
      <h3 [ngClass]="headingClass()">{{ primaryLabelKey() | translate }}</h3>
      <div
        class="rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-4 md:p-6 border-l-4 border-l-blue-500 space-y-4">
        @for (block of view.bodyBlocks; track $index) {
          <div>
            @if (block.label) {
              <p
                class="font-semibold text-slate-900 mb-2 m-0"
                [class.small]="embedMode">
                {{ block.label }}
              </p>
            }
            @for (line of block.lines; track $index) {
              <p
                class="mb-1 last:mb-0 text-slate-800 leading-relaxed m-0 font-serif text-base md:text-lg">
                {{ line }}
              </p>
            }
          </div>
        }
      </div>
    </section>
  }

  @if (hasList(view.learningOutcomes)) {
    <section>
      <h3 [ngClass]="headingClass()">{{ 'knowledgeSetup.sectionLearningOutcomes' | translate }}</h3>
      <ul class="m-0 ps-4 space-y-2 list-disc text-slate-700" [class.small]="embedMode">
        @for (o of view.learningOutcomes; track $index) {
          <li>{{ itemLabel(o) }}</li>
        }
      </ul>
    </section>
  }

  @if (hasList(view.materials)) {
    <section>
      <h3 [ngClass]="headingClass()">{{ 'knowledgeSetup.sectionMaterials' | translate }}</h3>
      <div class="grid gap-2 sm:grid-cols-2">
        @for (m of view.materials; track $index) {
          <div class="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-700">
            {{ itemLabel(m) }}
          </div>
        }
      </div>
    </section>
  }

  @if (showTeacherGuidance()) {
    <section>
      <h3 [ngClass]="headingClass()">{{ 'knowledgeSetup.sectionTeacherGuidance' | translate }}</h3>
      <p
        class="text-slate-700 m-0 leading-relaxed whitespace-pre-wrap"
        [class.small]="embedMode">
        {{ view.teacherGuidance }}
      </p>
    </section>
  }

  @if (showLegacyActivityInstructions()) {
    <section>
      <h3 [ngClass]="headingClass()">{{ 'knowledgeSetup.sectionActivityInstructions' | translate }}</h3>
      <ol class="m-0 ps-5 space-y-2 list-decimal text-slate-700" [class.small]="embedMode">
        @for (step of view.activityInstructions; track $index) {
          <li class="ps-1">{{ itemLabel(step) }}</li>
        }
      </ol>
    </section>
  }

  @if (view.pedagogicalGuidance) {
    <section>
      <h3 [ngClass]="headingClass()">{{ 'knowledgeSetup.sectionPedagogicalApproach' | translate }}</h3>
      <p
        class="text-slate-700 leading-relaxed whitespace-pre-wrap m-0"
        [class.small]="embedMode">
        {{ view.pedagogicalGuidance }}
      </p>
    </section>
  }

  @if (hasList(view.pedagogicalNotes)) {
    <section>
      <h3 [ngClass]="headingClass()">{{ 'knowledgeSetup.sectionPedagogicalNotes' | translate }}</h3>
      <div class="flex flex-col gap-2">
        @for (note of view.pedagogicalNotes; track $index) {
          <details class="edu-gen-details rounded-xl border border-slate-200 bg-white">
            <summary
              class="cursor-pointer select-none px-3 py-2 text-sm font-semibold text-slate-800">
              {{ 'knowledgeSetup.pedagogicalNoteItem' | translate }} {{ $index + 1 }}
            </summary>
            <div class="border-t border-slate-100 px-3 py-2 text-sm text-slate-600 leading-relaxed">
              {{ itemLabel(note) }}
            </div>
          </details>
        }
      </div>
    </section>
  }

  @if (hasList(view.culturalElements)) {
    <section>
      <h3 [ngClass]="headingClass()">{{ 'knowledgeSetup.sectionCulturalElements' | translate }}</h3>
      <div class="flex flex-wrap gap-2">
        @for (c of view.culturalElements; track $index) {
          <span
            class="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-900"
            >{{ itemLabel(c) }}</span
          >
        }
      </div>
    </section>
  }

  @if (languageSectionVisible()) {
    <section>
      <h3 [ngClass]="headingClass()">{{ 'knowledgeSetup.sectionLanguageIntegration' | translate }}</h3>
      @if (getMultilingualSupport(); as ms) {
        <p class="text-sm text-slate-600 mb-3 leading-relaxed" [class.small]="embedMode">{{ ms }}</p>
      }
      @if (hasList(getLocalWords())) {
        <div class="grid gap-2 sm:grid-cols-2">
          @for (w of getLocalWords(); track $index) {
            <div class="flex flex-col rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
              <span class="font-semibold text-slate-900">{{ w.word }}</span>
              <span class="text-slate-600">{{ w.translation }}</span>
            </div>
          }
        </div>
      }
      @for (k of languageExtraKeys(); track k) {
        <details class="edu-gen-details rounded-xl border border-slate-200 bg-white mt-2">
          <summary class="cursor-pointer select-none px-3 py-2 text-sm font-semibold text-slate-800">
            {{ k | eduFieldLabel }}
          </summary>
          <div class="border-t border-slate-100 px-3 py-2 text-sm text-slate-600">
            <app-educational-content-value-block
              [value]="languageExtraValue(k)"
              [depth]="0"
              [embedMode]="embedMode" />
          </div>
        </details>
      }
    </section>
  }

  @if (hasStandardsContent()) {
    <section>
      <h3 [ngClass]="headingClass()">{{ 'knowledgeSetup.sectionStandards' | translate }}</h3>
      <app-educational-content-value-block
        [value]="view.educationalStandardsAlignment"
        [depth]="0"
        [embedMode]="embedMode" />
    </section>
  }

  @if (hasList(view.safetyItems)) {
    <section class="flex flex-col gap-2">
      <h3
        [ngClass]="
          embedMode ? 'h6 fw-semibold mb-0' : 'text-lg font-bold text-slate-800 mb-1'
        ">
        {{ 'knowledgeSetup.sectionSafetyConsiderations' | translate }}
      </h3>
      @for (s of view.safetyItems; track $index) {
        <div
          class="rounded-xl border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-sm text-amber-950">
          {{ itemLabel(s) }}
        </div>
      }
    </section>
  }

  @if (hasList(view.inclusiveDesign)) {
    <section>
      <h3 [ngClass]="headingClass()">{{ 'knowledgeSetup.sectionInclusiveDesign' | translate }}</h3>
      <div class="flex flex-col gap-2">
        @for (row of view.inclusiveDesign; track $index) {
          @if (isRecordValue(row)) {
            <app-educational-content-value-block [value]="row" [depth]="0" [embedMode]="embedMode" />
          } @else {
            <p class="text-sm text-slate-700 m-0" [class.small]="embedMode">{{ itemLabel(row) }}</p>
          }
        }
      </div>
    </section>
  }

  @if (hasList(view.differentiation)) {
    <section>
      <h3 [ngClass]="headingClass()">{{ 'knowledgeSetup.sectionDifferentiation' | translate }}</h3>
      <ul class="m-0 ps-4 space-y-2 list-disc text-slate-700 text-sm">
        @for (d of view.differentiation; track $index) {
          <li>{{ itemLabel(d) }}</li>
        }
      </ul>
    </section>
  }

  @if (hasRawContent()) {
    <section>
      <h3 [ngClass]="headingClass()">{{ 'knowledgeSetup.sectionStructuredContent' | translate }}</h3>
      <details class="edu-gen-details rounded-xl border border-slate-200 bg-white" open>
        <summary class="cursor-pointer select-none px-3 py-2 text-sm font-semibold text-slate-800">
          {{ 'knowledgeSetup.sectionStructuredContentSummary' | translate }}
        </summary>
        <div class="border-t border-slate-100 px-3 py-2 text-sm text-slate-600">
          <app-educational-content-value-block [value]="view.rawContent" [depth]="0" [embedMode]="embedMode" />
        </div>
      </details>
    </section>
  }

  @if (extrasEntries().length > 0) {
    <section>
      <h3 [ngClass]="headingClass()">{{ 'knowledgeSetup.sectionAdditional' | translate }}</h3>
      <div class="flex flex-col gap-2">
        @for (kv of extrasEntries(); track $index) {
          <details class="edu-gen-details rounded-xl border border-slate-200 bg-white">
            <summary class="cursor-pointer select-none px-3 py-2 text-sm font-semibold text-slate-800">
              {{ kv[0] | eduFieldLabel }}
            </summary>
            <div class="border-t border-slate-100 px-3 py-2 text-sm text-slate-600">
              <app-educational-content-value-block [value]="kv[1]" [depth]="0" [embedMode]="embedMode" />
            </div>
          </details>
        }
      </div>
    </section>
  }
</div>
